import React, { useState, useEffect } from 'react';
import {
  Code,
  Database,
  Globe,
  Layers,
  Server,
  Smartphone,
} from 'lucide-react';
import techStackData from '@/data/tech-stack.json';

interface TechStackItem {
  id: string;
  category: string;
  technologies: string[];
  display_order: number;
  is_active: boolean;
}

const TechStack = () => {
  const [techStacks, setTechStacks] = useState<TechStackItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Load static data
    const data = (techStackData as TechStackItem[])
      .filter(t => t.is_active)
      .sort((a, b) => (a.display_order || 0) - (b.display_order || 0))
    setTechStacks(data)
    setLoading(false)
  }, []);

  // Icon mapping for categories
  const getCategoryIcon = (category: string) => {
    const lowerCategory = category.toLowerCase();
    if (lowerCategory.includes('frontend') || lowerCategory.includes('ui'))
      return Globe;
    if (lowerCategory.includes('backend') || lowerCategory.includes('server'))
      return Server;
    if (lowerCategory.includes('database') || lowerCategory.includes('db'))
      return Database;
    if (lowerCategory.includes('mobile')) return Smartphone;
    if (lowerCategory.includes('framework')) return Layers;
    return Code;
  };

  // Color mapping for categories
  const getCategoryColor = (index: number) => {
    const colors = [
      'from-blue-500 to-cyan-500',
      'from-purple-500 to-pink-500',
      'from-green-500 to-emerald-500',
      'from-orange-500 to-red-500',
      'from-indigo-500 to-purple-500',
      'from-teal-500 to-green-500',
    ];
    return colors[index % colors.length];
  };

  if (loading) {
    return (
      <section className="section-padding bg-gradient-to-br from-slate-50 to-blue-50/50">
        <div className="container-custom">
          <div className="text-center">
            <div className="animate-pulse space-y-4">
              <div className="h-8 bg-gray-300 rounded w-64 mx-auto"></div>
              <div className="h-4 bg-gray-300 rounded w-96 mx-auto"></div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-8">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="h-40 bg-gray-300 rounded-xl"></div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Fallback to static data if no dynamic data is available
  const displayData =
    techStacks.length > 0
      ? techStacks
      : [
          {
            id: 'frontend',
            category: 'Frontend',
            technologies: ['Vue.js', 'React.js', 'JavaScript'],
            display_order: 1,
            is_active: true,
          },
          {
            id: 'backend',
            category: 'Backend',
            technologies: [
              'Spring Boot',
              'PHP',
              'Python',
              'Java',
              'Django',
              'Tendenci',
            ],
            display_order: 2,
            is_active: true,
          },
          {
            id: 'cms',
            category: 'CMS',
            technologies: ['WordPress', 'Magnolia CMS'],
            display_order: 3,
            is_active: true,
          },
          {
            id: 'frameworks',
            category: 'Frameworks',
            technologies: ['Apache OFBiz', 'Groovy'],
            display_order: 4,
            is_active: true,
          },
        ];

  return (
    <section className="section-padding bg-gradient-to-br from-slate-50 to-blue-50/50 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-20 left-20 w-32 h-32 bg-primary rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-20 w-40 h-40 bg-purple-500 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-60 h-60 bg-blue-400 rounded-full blur-3xl"></div>
      </div>

      <div className="container-custom relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-16 animate-on-scroll">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 text-foreground font-heading">
            Our Tech Stack
          </h2>
          <div className="h-1 w-20 bg-primary mx-auto mb-6"></div>
          <p className="text-lg md:text-xl text-muted-foreground">
            We leverage cutting-edge technologies to deliver robust, scalable,
            and innovative solutions.
          </p>
        </div>

        {/* Tech Stack Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {displayData.map((stack, index) => {
            const IconComponent = getCategoryIcon(stack.category);
            const gradientColor = getCategoryColor(index);

            return (
              <div
                key={stack.id}
                className="group relative animate-on-scroll"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                {/* Main Card */}
                <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 border border-white/50 backdrop-blur-sm relative overflow-hidden h-full">
                  {/* Gradient Background on Hover */}
                  <div
                    className={`absolute inset-0 bg-gradient-to-br ${gradientColor} opacity-0 group-hover:opacity-5 transition-opacity duration-500 rounded-2xl`}
                  ></div>

                  {/* Icon with Gradient Background */}
                  <div
                    className={`relative mb-6 w-16 h-16 bg-gradient-to-br ${gradientColor} rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300`}
                  >
                    <IconComponent className="w-8 h-8 text-white" />
                  </div>

                  {/* Category Title */}
                  <h3 className="text-xl font-bold mb-6 text-foreground group-hover:text-primary transition-colors duration-300">
                    {stack.category}
                  </h3>

                  {/* Technologies List */}
                  <div className="space-y-3">
                    {stack.technologies.map((tech, techIndex) => (
                      <div
                        key={techIndex}
                        className="flex items-center group/tech hover:translate-x-1 transition-transform duration-200"
                      >
                        <div
                          className={`w-2 h-2 bg-gradient-to-r ${gradientColor} rounded-full mr-3 group-hover/tech:scale-125 transition-transform duration-200`}
                        ></div>
                        <span className="text-muted-foreground group-hover/tech:text-foreground transition-colors duration-200 font-medium">
                          {tech}
                        </span>
                      </div>
                    ))}
                  </div>

                  {/* Decorative Elements */}
                  <div className="absolute -top-2 -right-2 w-20 h-20 bg-gradient-to-br from-white/20 to-transparent rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                  <div className="absolute -bottom-4 -left-4 w-24 h-24 bg-gradient-to-tr from-primary/5 to-transparent rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                </div>

                {/* Floating Shadow */}
                <div className="absolute inset-0 bg-gradient-to-br from-primary/10 to-purple-500/10 rounded-2xl blur-xl opacity-0 group-hover:opacity-60 transition-opacity duration-500 -z-10 scale-110"></div>
              </div>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-16 animate-on-scroll">
          <div className="inline-flex items-center justify-center space-x-2 text-muted-foreground">
            <div className="w-8 h-px bg-gradient-to-r from-transparent to-primary"></div>
            <span className="font-medium">Powered by Innovation</span>
            <div className="w-8 h-px bg-gradient-to-l from-transparent to-primary"></div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TechStack;
